import { useAuth } from '@clerk/clerk-react';
import {
  PageContainer,
  Button,
  Badge,
  DataTable,
  type DataTableColumn,
  type DataTableAction,
  ConfirmDialog,
} from '@startupquickstart/frontend';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileSpreadsheet, Clock, CheckCircle, XCircle, Loader2, Download, PlayCircle, Ban } from 'lucide-react';
import { toast } from '@startupquickstart/frontend';
import { CsvImportWizard } from '@/components/import/CsvImportWizard';
import { cn } from '@/lib/utils';

interface ImportSession {
  id: string;
  entityType: 'attraction' | 'neighborhood' | 'tour';
  fileName: string;
  totalRows: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
  status: string;
  createdAt: string;
  completedAt: string | null;
}

const entityTypeLabels: Record<string, string> = {
  attraction: 'Attraction',
  neighborhood: 'Neighborhood',
  tour: 'Tour',
};

function ImportStatusBadge({ status }: { status: string }) {
  const config: Record<
    string,
    { className: string; icon: React.ReactNode }
  > = {
    pending: {
      className: 'bg-muted text-muted-foreground',
      icon: <Clock className="h-3 w-3" />,
    },
    validating: {
      className: 'bg-primary/10 text-primary',
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
    },
    awaiting_confirmation: {
      className: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
      icon: <Clock className="h-3 w-3" />,
    },
    validated: {
      className: 'bg-primary/10 text-primary',
      icon: <CheckCircle className="h-3 w-3" />,
    },
    importing: {
      className: 'bg-primary/10 text-primary',
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
    },
    completed: {
      className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
      icon: <CheckCircle className="h-3 w-3" />,
    },
    failed: {
      className: 'bg-destructive/10 text-destructive',
      icon: <XCircle className="h-3 w-3" />,
    },
    cancelled: {
      className: 'bg-muted text-muted-foreground',
      icon: <Ban className="h-3 w-3" />,
    },
  };

  const { className, icon } = config[status] || {
    className: 'bg-muted text-muted-foreground',
    icon: null,
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize',
        className
      )}
    >
      {icon}
      {status.replace('_', ' ')}
    </span>
  );
}

export function ImportPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [sessions, setSessions] = useState<ImportSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cancelSession, setCancelSession] = useState<ImportSession | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const fetchSessions = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await fetch('/api/v1/import/sessions?limit=20', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch import sessions');
      }

      const result = await response.json();
      setSessions(result.data || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Failed to load import history');
    } finally {
      setIsLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    // Fetch sessions on mount and when wizard closes
    if (!isWizardOpen) {
      fetchSessions();
    }
  }, [fetchSessions, isWizardOpen]);

  const downloadErrors = useCallback(
    async (sessionId: string) => {
      try {
        const token = await getToken();
        const response = await fetch(`/api/v1/import/sessions/${sessionId}/errors`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to download errors');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `import-errors-${sessionId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch {
        toast.error('Failed to download error report');
      }
    },
    [getToken]
  );

  const handleImportComplete = useCallback(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleCancelSession = useCallback(async () => {
    if (!cancelSession) return;
    setIsCancelling(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/v1/import/sessions/${cancelSession.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to cancel import');
      }
      toast.success('Import cancelled');
      setCancelSession(null);
      fetchSessions();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to cancel import');
    } finally {
      setIsCancelling(false);
    }
  }, [cancelSession, getToken, fetchSessions]);

  // Column definitions for DataTable
  const columns: DataTableColumn<ImportSession>[] = useMemo(
    () => [
      {
        key: 'fileName',
        label: 'File',
        sortable: true,
        format: (value: string) => <span className="font-medium">{value}</span>,
      },
      {
        key: 'entityType',
        label: 'Type',
        sortable: true,
        format: (value: string) => (
          <Badge variant="outline" className="font-normal">
            {entityTypeLabels[value] || value}
          </Badge>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        format: (value: string) => <ImportStatusBadge status={value} />,
      },
      {
        key: 'totalRows',
        label: 'Rows',
        sortable: true,
        hideOnMobile: true,
        format: (value: number) => (
          <span className="text-muted-foreground font-mono text-sm">{value}</span>
        ),
      },
      {
        key: 'successCount',
        label: 'Success',
        sortable: true,
        hideOnMobile: true,
        format: (value: number) => (
          <span className="text-emerald-600 dark:text-emerald-400 font-medium font-mono text-sm">
            {value}
          </span>
        ),
      },
      {
        key: 'errorCount',
        label: 'Errors',
        sortable: true,
        hideOnMobile: true,
        format: (value: number) => (
          <span
            className={cn(
              'font-mono text-sm',
              value > 0 ? 'text-destructive font-medium' : 'text-muted-foreground'
            )}
          >
            {value}
          </span>
        ),
      },
      {
        key: 'createdAt',
        label: 'Date',
        sortable: true,
        type: 'datetime',
        hideOnMobile: true,
      },
    ],
    []
  );

  // Actions for DataTable rows
  const actions: DataTableAction<ImportSession>[] = useMemo(
    () => [
      {
        key: 'view-details',
        label: 'View Details',
        onClick: (session) => {
          navigate(`/import/${session.id}`);
        },
        getUrl: (session) => `/import/${session.id}`,
      },
      {
        key: 'resume',
        label: 'Resume Import',
        icon: PlayCircle,
        onClick: (session) => {
          navigate(`/import/${session.id}`, { state: { resume: true } });
        },
        visible: (session: ImportSession) =>
          session.status === 'awaiting_confirmation' || session.status === 'validated',
      },
      {
        key: 'download-errors',
        label: 'Download Errors',
        icon: Download,
        onClick: (session) => {
          if (session.errorCount > 0 && session.status === 'completed') {
            downloadErrors(session.id);
          }
        },
        visible: (session: ImportSession) =>
          session.errorCount > 0 && session.status === 'completed',
      },
      {
        key: 'cancel',
        label: 'Cancel Import',
        icon: Ban,
        onClick: (session) => {
          setCancelSession(session);
        },
        visible: (session: ImportSession) =>
          ['pending', 'awaiting_confirmation', 'validated'].includes(session.status),
      },
    ],
    [downloadErrors, navigate]
  );

  // Handle row click to navigate to detail page
  const handleRowClick = useCallback(
    (session: ImportSession) => {
      navigate(`/import/${session.id}`);
    },
    [navigate]
  );

  // If wizard is open, render it instead of the main page content
  if (isWizardOpen) {
    return (
      <PageContainer>
        <CsvImportWizard
          onCancel={() => setIsWizardOpen(false)}
          onImportComplete={handleImportComplete}
        />
      </PageContainer>
    );
  }

  // Empty state for DataTable
  const emptyState = (
    <div className="py-16 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
        <FileSpreadsheet className="h-8 w-8 text-primary" />
      </div>
      <p className="text-lg font-medium tracking-tight">No import history yet</p>
      <p className="text-sm text-muted-foreground mt-1 mb-6">
        Start by importing your first CSV file.
      </p>
      <Button onClick={() => setIsWizardOpen(true)} className="gap-2">
        <Upload className="h-4 w-4" />
        Start Import
      </Button>
    </div>
  );

  return (
    <PageContainer>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Import Data</h1>
            <p className="text-muted-foreground mt-1">
              Bulk import attractions, neighborhoods, and tours from CSV files.
            </p>
          </div>
          <Button onClick={() => setIsWizardOpen(true)} className="gap-2 w-full sm:w-auto">
            <Upload className="h-4 w-4" />
            New Import
          </Button>
        </div>

        {/* Import History */}
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <DataTable<ImportSession>
            columns={columns}
            data={sessions}
            loading={isLoading}
            emptyState={emptyState}
            actions={actions}
            skeletonRows={5}
            onRowClick={handleRowClick}
          />
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={!!cancelSession}
        onOpenChange={(open) => !open && setCancelSession(null)}
        title="Cancel Import"
        description={`Are you sure you want to cancel the import "${cancelSession?.fileName}"? This action cannot be undone and any pending rows will not be processed.`}
        confirmText="Cancel Import"
        cancelText="Keep Import"
        variant="destructive"
        loading={isCancelling}
        onConfirm={handleCancelSession}
      />
    </PageContainer>
  );
}
